import React, { useState, useEffect } from 'react';
import useSamplingEvent from 'src/app/samples/hooks/useSamplingEvent';
import {
    useLockEditingSampleKitMutation,
    useUnlockEditingSampleKitMutation,
} from 'src/app/samples/sample-manifest/state/api/sampleManifestGraphSlice';
import { getIsSavingSamplingKit } from 'src/app/samples/sample-manifest/state/sampleManifestSelector';
import { setIsSavingSamplingKit } from 'src/app/samples/sample-manifest/state/sampleManifestSlice';
import SelectMultiple from 'src/shared/components/select-multiple/SelectMultiple';
import WarningMessage from 'src/shared/components/warning-message/WarningMessage';
import { getTestTypes } from 'src/shared/helpers/kitHelpers';
import { CMSHabitatAssayTypeWithoutImage, SamplingKitStatus } from 'src/shared/types';
import { useAppDispatch, useAppSelector } from 'src/store';
import useSampleManifestAddTestsAnalytics from 'src/app/samples/sample-manifest/hooks/useSampleManifestAddTestsAnalytics';

type AddTestsDropdownProps = {
    samplingKitId: string;
};

type HabitatTests = {
    remaining: number;
    testType: CMSHabitatAssayTypeWithoutImage;
    total: number;
};

const AddTestsDropdown = ({ samplingKitId }: AddTestsDropdownProps) => {
    const { samplingEvent, remainingTests, fieldDataCompletedKits, saveSamplingKit, isBatchOnlySubmit, isKitInAnyBatch, currentProjectId } =
        useSamplingEvent();
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [habitatTests, setHabitatTests] = useState<HabitatTests[]>([]);
    const samplingKit = fieldDataCompletedKits.find(kit => kit.id === samplingKitId);
    const [lockEditingSampleKit] = useLockEditingSampleKitMutation();
    const [unlockEditingSampleKit] = useUnlockEditingSampleKitMutation();
    const [isLockObtained, setIsLockObtained] = useState<boolean>();
    const [isOpen, setIsOpen] = useState(false);
    const isSaving = useAppSelector(getIsSavingSamplingKit);
    const dispatch = useAppDispatch();
    const { trackSampleManifestAddTestsAddDropdownAction } = useSampleManifestAddTestsAnalytics();

    //disable form if the kit is in a batch and not in batch submission mode
    const isDisabled = [
        isKitInAnyBatch(samplingKitId) && !isBatchOnlySubmit,
        samplingKit?.status === SamplingKitStatus.REJECTED_BY_LAB,
        isSaving,
    ].some(Boolean);

    useEffect(() => {
        if (samplingKit) {
            setSelectedValues(testTypes.map(testType => testType.habitatAssayKey));
        }
    }, [samplingKit?.testTypes]);

    if (!samplingKit) {
        return null;
    }

    const { habitat, testTypes } = samplingKit;
    const habitatAssayTypeOptions = habitatTests.map(test => ({
        label: (
            <div className='flex flex-col'>
                <span className=''>{test.testType.habitatName}</span>
                <div className='flex gap-4 justify-between items-center'>
                    <span className='font-bold'>{test.testType.assayName}</span>
                    <span className='text-primary'>{test.remaining}</span>
                </div>
            </div>
        ),
        value: test.testType.habitatAssayKey,
        isDisabled:
            //disable if the test is not already added and total is greater than 3 and remianing is 0
            (test.remaining < 1 && !selectedValues.includes(test.testType.habitatAssayKey)) ||
            (selectedValues.length >= 3 && !selectedValues.includes(test.testType.habitatAssayKey)) ||
            isLockObtained === false,
    }));

    const handleChange = (values: string[]) => {
        // Capture GA for sample manifest add tests - add dropdown change
        trackSampleManifestAddTestsAddDropdownAction({ action: values.length < selectedValues.length ? 'test_removed' : 'test_added' });

        setSelectedValues(values);
    };

    const handleClose = async () => {
        setIsOpen(false);

        if (!isLockObtained) {
            return;
        }

        await unlockEditingSampleKit({
            projectId: currentProjectId || '',
            eventIdentifier: samplingEvent?.identifier || '',
            kitId: samplingKit?.id,
        });

        const newTestTypes = habitatTests.filter(test => selectedValues.includes(test.testType.habitatAssayKey)).map(test => test.testType);

        // Prevent saving if there are no changes and unlock the kit
        if (
            selectedValues.length === testTypes.length &&
            selectedValues.every(value => testTypes.some(testType => testType.habitatAssayKey === value))
        ) {
            return;
        }

        try {
            dispatch(setIsSavingSamplingKit(true));
            await saveSamplingKit({
                ...samplingKit,
                testTypes: newTestTypes,
            });
        } finally {
            dispatch(setIsSavingSamplingKit(false));
        }
    };

    const handleOpen = () => {
        if (!isOpen) {
            setIsOpen(true);
            if (!samplingKit || !samplingEvent) {
                return;
            }
            const startEditing = async () => {
                const response = await lockEditingSampleKit({
                    projectId: currentProjectId || '',
                    eventIdentifier: samplingEvent?.identifier,
                    kitId: samplingKit?.id,
                }).unwrap();

                setIsLockObtained(response.lock);
            };
            startEditing();
        }
        const testTypes = getTestTypes(samplingKit.barcode, habitat);
        const habitatTests = remainingTests.filter(
            test => test.testType.habitatName === habitat && testTypes?.some(tt => tt.habitatAssayKey === test.testType.habitatAssayKey)
        );

        // Capture GA for sample manifest add tests - add dropdown click
        trackSampleManifestAddTestsAddDropdownAction({ action: 'click' });

        setHabitatTests(habitatTests);
    };

    const editLockedWarningMessage = (
        <WarningMessage
            message={
                <span>
                    This sample is being edited by <br />
                    another user, please try later.
                </span>
            }
        />
    );

    const noTestsWarningMessage = (
        <WarningMessage
            message={
                <span>
                    There are no applicable tests within <br />
                    this event for this kit <br />
                    type
                </span>
            }
        />
    );

    const headerContent = isLockObtained === false ? editLockedWarningMessage : habitatTests.length > 0 ? null : noTestsWarningMessage;

    return (
        <SelectMultiple
            options={habitatAssayTypeOptions}
            onChange={handleChange}
            values={selectedValues}
            onClose={handleClose}
            onOpen={handleOpen}
            noSelectionText='Add'
            showSelectAll={false}
            footerContent='Select up to 3 tests'
            headerContent={headerContent}
            disabled={isDisabled}
        />
    );
};

export default AddTestsDropdown;
